import React, { useState, useEffect, useCallback } from "react";
import { Box, Typography, CircularProgress, Alert } from "@mui/material";

import PermissionTable from "./PermissionComponents/PermissionTable";
import PermissionFilters from "./PermissionComponents/PermissionFilters";
import PermissionModal from "./PermissionComponents/PermissionModal";
import ConfirmationModal from "./PermissionComponents/ConfirmationModal";
import FeedbackModal from "./PermissionComponents/FeedbackModal";
import { Heading } from "../../../components/styledConstants";
import {
  fetchPermissionsRequest,
  createPermissionRequest,
  editPermissionRequest,
  togglePermissionStatusRequest,
} from "../../../utils/api";

const PermissionManagement = () => {
  // State for ALL permissions fetched from the API
  const [allPermissions, setAllPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  // State for pagination
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // State for immediate filter values (for client-side search)
  const [nameFilter, setNameFilter] = useState("");
  const [descriptionFilter, setDescriptionFilter] = useState("");

  // Function to fetch permissions
  // In the future, you'll pass debounced filters to this function
  const fetchPermissions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // For now, we fetch all permissions to enable client-side search
      const response = await fetchPermissionsRequest(currentPage, itemsPerPage, nameFilter, descriptionFilter);
      const { data, totalRecords, totalPages } = response.data.data;
      console.log(data);
      const fetchedData = Array.isArray(data) ? data : [];

      setAllPermissions(fetchedData);
      setTotalRecords(totalRecords);
      setTotalPages(totalPages);
    } catch (err) {
      setError(err.message || "Failed to fetch permissions. Please try again.");
      setAllPermissions([]);
      setTotalRecords(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, nameFilter, descriptionFilter]);

  // Data fetching effect
  // This hook will be responsible for calling the API
  useEffect(() => {
    // For now, this only runs on initial render.
    // In the future, you can add debounced filters to the dependency array
    // to trigger server-side search.
    fetchPermissions();
  }, [fetchPermissions]);

  // Client-side filtering logic
  const filteredPermissions = allPermissions.filter((permission) => {
    const nameMatches = permission.name.toLowerCase().includes(nameFilter.toLowerCase());
    const descriptionMatches = permission.description.toLowerCase().includes(descriptionFilter.toLowerCase());
    return nameMatches && descriptionMatches;
  });

  // State for modals
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentPermission, setCurrentPermission] = useState({ name: "", description: "" });
  const [feedbackModal, setFeedbackModal] = useState({ open: false, title: "", message: "", type: "success" });
  const [confirmationModal, setConfirmationModal] = useState({
    open: false,
    title: "",
    message: "",
    onConfirm: () => {},
  });

  // Handlers for create, edit, and toggle
  const handleCreateClick = () => {
    setIsModalOpen(true);
    setIsEditMode(false);
    setCurrentPermission({ name: "", description: "" });
  };

  const handleEditClick = (permission) => {
    setIsModalOpen(true);
    setIsEditMode(true);
    setCurrentPermission(permission);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setCurrentPermission({ name: "", description: "" });
  };

  const handleFormSubmit = async () => {
    setLoading(true);
    setError(null);
    try {
      if (isEditMode) {
        await editPermissionRequest(currentPermission.name, currentPermission.description);
        setFeedbackModal({
          open: true,
          title: "Success",
          message: `Permission '${currentPermission.name}' updated successfully.`,
          type: "success",
        });
      } else {
        await createPermissionRequest(currentPermission);
        setFeedbackModal({
          open: true,
          title: "Success",
          message: `Permission '${currentPermission.name}' created successfully.`,
          type: "success",
        });
      }
      handleCloseModal();
      // Re-fetch all permissions to update the list after a change
      fetchPermissions();
    } catch (err) {
      setError(err.message || `Failed to ${isEditMode ? "update" : "create"} permission.`);
      setFeedbackModal({
        open: true,
        title: "Error",
        message: err.message || `Failed to ${isEditMode ? "update" : "create"} permission.`,
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmToggleStatus = async (permissionName, newStatus) => {
    setLoading(true);
    try {
      await togglePermissionStatusRequest(permissionName, newStatus);
      setFeedbackModal({
        open: true,
        title: "Success",
        message: `Status for '${permissionName}' toggled to ${newStatus}.`,
        type: "success",
      });
      // Re-fetch all permissions to update the list after a change
      fetchPermissions();
    } catch (err) {
      setFeedbackModal({
        open: true,
        title: "Error",
        message: err.message || `Failed to toggle status for '${permissionName}'.`,
        type: "error",
      });
    } finally {
      setLoading(false);
      setConfirmationModal({ ...confirmationModal, open: false });
    }
  };

  const handleToggleStatus = (permissionName, currentStatus) => {
    const newStatus = currentStatus === "ACTIVE" ? "INACTIVE" : "ACTIVE";
    setConfirmationModal({
      open: true,
      title: "Confirm Status Change",
      message: `Are you sure you want to change the status of '${permissionName}' to ${newStatus}?`,
      onConfirm: () => handleConfirmToggleStatus(permissionName, newStatus),
    });
  };

  // Pagination and filter handlers
  const handlePageChange = (page) => {
    if (typeof page === "number" && page > 0) {
      setCurrentPage(page - 1);
    }
  };
  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value, 10));
    setCurrentPage(0);
  };

  const handleNameFilterChange = (e) => {
    setNameFilter(e.target.value);
    setCurrentPage(0); // Reset to first page on filter change
  };
  const handleDescriptionFilterChange = (e) => {
    setDescriptionFilter(e.target.value);
    setCurrentPage(0); // Reset to first page on filter change
  };

  const handleClearFilters = () => {
    setNameFilter("");
    setDescriptionFilter("");
    setCurrentPage(0);
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return "";
    try {
      const date = new Date(isoString);
      return date.toLocaleString("en-IN", {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    } catch (e) {
      return isoString;
    }
  };

  return (
    <>
      <div className="flex flex-center space-between mb10">
        <Heading size="xxl">Permission Management</Heading>
      </div>
      <Box sx={{ p: 3, maxWidth: 1200, margin: "auto", bgcolor: "background.paper", borderRadius: 2, boxShadow: 3 }}>
        <PermissionFilters
          nameFilter={nameFilter}
          descriptionFilter={descriptionFilter}
          handleNameFilterChange={handleNameFilterChange}
          handleDescriptionFilterChange={handleDescriptionFilterChange}
          handleClearFilters={handleClearFilters}
          handleCreateClick={handleCreateClick}
        />

        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {error && (
          <Alert severity="error" sx={{ my: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && filteredPermissions.length === 0 ? (
          <Alert severity="info" sx={{ my: 2 }}>
            {nameFilter || descriptionFilter
              ? "No permissions found matching your filter."
              : "No permissions found. Create a new permission to get started."}
          </Alert>
        ) : (
          <PermissionTable
            permissions={allPermissions}
            handleEditClick={handleEditClick}
            handleToggleStatus={handleToggleStatus}
            formatDateTime={formatDateTime}
            currentPage={currentPage + 1}
            totalPages={totalPages}
            totalRecords={totalRecords}
            itemsPerPage={itemsPerPage}
            handlePageChange={handlePageChange}
            handleItemsPerPageChange={handleItemsPerPageChange}
          />
        )}

        <PermissionModal
          isModalOpen={isModalOpen}
          isEditMode={isEditMode}
          currentPermission={currentPermission}
          setCurrentPermission={setCurrentPermission}
          handleFormSubmit={handleFormSubmit}
          handleCloseModal={handleCloseModal}
        />
      </Box>

      <ConfirmationModal
        open={confirmationModal.open}
        title={confirmationModal.title}
        message={confirmationModal.message}
        onClose={() => setConfirmationModal({ ...confirmationModal, open: false })}
        onConfirm={confirmationModal.onConfirm}
        loading={loading}
        cancelText="Cancel"
        confirmText="Confirm"
      />
      <FeedbackModal
        open={feedbackModal.open}
        title={feedbackModal.title}
        message={feedbackModal.message}
        onClose={() => setFeedbackModal({ ...feedbackModal, open: false })}
        type={feedbackModal.type}
      />
    </>
  );
};

export default PermissionManagement;
